<template>
  <LexmeaModal
    :fullscreenOnMobile="true"
    @beforeOpen="beforeOpen"
    @beforeClose="linkList?.resetLinks"
    @closed="$emit('closed')"
  >
    <template #title
      >{{ $t("legal_text.reference.create_reference") }}
    </template>
    <template #default>
      <LinkList
        ref="linkList"
        v-model="selectedLinks"
        :saving
        :legalText
        :paragraph
      />
      <div class="mt-5 flex items-center justify-between">
        <button class="flex items-center" @click="linkList?.handleAdd">
          <i-heroicons-plus-circle />
          <span class="ml-1 text-xs font-bold md:text-sm">{{
            $t("legal_text.reference.add_another_norm")
          }}</span>
        </button>
        <LexmeaButton
          :loading="saving"
          :disabled="!linkDataValid"
          @click="handleSave"
        >
          {{ $t("legal_text.reference.save") }}
        </LexmeaButton>
      </div>
    </template>
  </LexmeaModal>
</template>

<script lang="ts" setup>
import { LinkClient } from "~/api/link";
import LinkList from "./LinkList.vue";
import type { IUpdatableLinkChain } from "~/models/IUserEntry";
import type { ISelect } from "~/models/ISelect";
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

const emit = defineEmits<{
  save: [paragraph: ILegalTextParagraph, links: IUpdatableLinkChain["links"]];
  closed: [];
}>();

const props = defineProps<{
  saving: boolean;
  legalText: ILegalText;
  paragraph: ILegalTextParagraph;
  editingLinkChain: IUpdatableLinkChain | undefined;
}>();

const paragraph = computed(() =>
  props.editingLinkChain
    ? props.editingLinkChain.legal_text_paragraph
    : props.paragraph
);

const selectedLinks = ref<IUpdatableLinkChain["links"]>();

const linkList = ref<InstanceType<typeof LinkList> | null>(null);
const linkClient = useApiClient(LinkClient);

const linkDataValid = computed(() =>
  selectedLinks.value?.every((link) => link.legal_text_paragraph_linked_id)
);
const linkTypes = ref<ISelect[]>([]);

onMounted(async () => {
  const types = await linkClient.fetchLinkTypes();
  linkTypes.value = types.map((type) => ({
    label: type.name,
    value: type.id,
  }));
});

const beforeOpen = () => {
  if (props.editingLinkChain?.links) {
    selectedLinks.value = [...props.editingLinkChain.links];
  }
};

const handleSave = () => {
  if (!selectedLinks.value) {
    return;
  }
  emit("save", paragraph.value, selectedLinks.value);
};
</script>
